<template>
  <div ref="chartRef" class="chart-box"></div>
</template>

<script setup>
import * as echarts from "echarts";
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  option: {
    type: Object,
    required: true,
  },
});

const chartRef = ref(null);
let chart = null;

const MIN_LEGEND_FONT_SIZE = 26;
const MIN_AXIS_FONT_SIZE = 24;

function withMinFontSize(textStyle = {}, fontSize) {
  return {
    ...textStyle,
    fontSize: Math.max(Number(textStyle.fontSize) || 0, fontSize),
  };
}

function normalizeLegend(legend) {
  if (!legend) return legend;
  if (Array.isArray(legend)) {
    return legend.map((item) => ({
      ...item,
      textStyle: withMinFontSize(item.textStyle, MIN_LEGEND_FONT_SIZE),
    }));
  }
  return {
    ...legend,
    textStyle: withMinFontSize(legend.textStyle, MIN_LEGEND_FONT_SIZE),
  };
}

function normalizeAxis(axis) {
  if (!axis) return axis;
  if (Array.isArray(axis)) {
    return axis.map((item) => ({
      ...item,
      axisLabel: {
        ...item.axisLabel,
        fontSize: Math.max(Number(item.axisLabel?.fontSize) || 0, MIN_AXIS_FONT_SIZE),
      },
    }));
  }
  return {
    ...axis,
    axisLabel: {
      ...axis.axisLabel,
      fontSize: Math.max(Number(axis.axisLabel?.fontSize) || 0, MIN_AXIS_FONT_SIZE),
    },
  };
}

function hideZeroYAxisLabel(yAxis) {
  if (!yAxis) return yAxis;
  const normalizeItem = (item) => {
    const originFormatter = item.axisLabel?.formatter;
    return {
      ...item,
      axisLabel: {
        ...item.axisLabel,
        formatter(value, index) {
          if (Number(value) === 0) return "";
          if (typeof originFormatter === "function") {
            return originFormatter(value, index);
          }
          if (typeof originFormatter === "string") {
            return originFormatter.replace("{value}", value);
          }
          return value;
        },
      },
    };
  };
  return Array.isArray(yAxis) ? yAxis.map(normalizeItem) : normalizeItem(yAxis);
}

function normalizeOption(option) {
  const yAxis = normalizeAxis(option.yAxis);
  return {
    ...option,
    legend: normalizeLegend(option.legend),
    xAxis: normalizeAxis(option.xAxis),
    yAxis: hideZeroYAxisLabel(yAxis),
  };
}

function renderChart() {
  if (!chartRef.value) return;
  if (!chart) {
    chart = echarts.init(chartRef.value);
  }
  chart.setOption(normalizeOption(props.option), true);
}

function resizeChart() {
  chart && chart.resize();
}

watch(
  () => props.option,
  () => nextTick(renderChart),
  { deep: true }
);

onMounted(() => {
  nextTick(renderChart);
  window.addEventListener("resize", resizeChart);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  chart && chart.dispose();
  chart = null;
});
</script>

<style scoped>
.chart-box {
  width: 100%;
  height: 100%;
}
</style>
